import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { Logo, Wordmark } from "../shared/Logo";
import { ThemeToggle } from "../shared/ThemeToggle";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { LayoutDashboard, FileText, Rocket, Receipt, MessageSquare, FolderOpen, LogOut, Menu, X } from "lucide-react";
import { AISidePanel } from "../shared/AISidePanel";

const NAV = [
  { to: "/portal", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/portal/quotes", label: "My Quotes", icon: FileText },
  { to: "/portal/jobs", label: "My Jobs", icon: Rocket },
  { to: "/portal/invoices", label: "Invoices & Payments", icon: Receipt },
  { to: "/portal/messages", label: "Support", icon: MessageSquare },
  { to: "/portal/documents", label: "Documents", icon: FolderOpen },
];

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background noise-cc">
      <header className="sticky top-0 z-40 glass-cc bg-background/80 border-b">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5" data-testid="portal-logo-link">
            <Logo size={24} />
            <div>
              <Wordmark className="h-[10px]" />
              <p className="text-[9px] font-mono-cc tracking-[0.25em] text-muted-foreground uppercase">Customer Portal</p>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                data-testid={`portal-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-full text-[13px] font-medium transition-colors duration-200 ${
                    isActive ? "bg-[var(--cc-accent)]/10 text-[var(--cc-accent)]" : "text-muted-foreground hover:text-foreground"
                  }`}>
                <n.icon className="w-4 h-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button data-testid="portal-new-quote-button" size="sm" className="rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold hidden sm:inline-flex" onClick={() => navigate("/book")}>
              Book Service
            </Button>
            <button data-testid="portal-logout-button" onClick={handleLogout} className="p-2 rounded-full border hover:border-[var(--cc-accent)] transition-colors duration-200" aria-label="Log out">
              <LogOut className="w-4 h-4" />
            </button>
            <button className="md:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="portal-mobile-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {open && (
          <nav className="md:hidden border-t bg-background px-5 py-3 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
                className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                <n.icon className="w-4 h-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>
      <main className="relative z-10 max-w-6xl mx-auto px-5 py-8">
        <p className="text-sm text-muted-foreground mb-6" data-testid="portal-welcome-text">
          Welcome back, <span className="text-foreground font-semibold">{user?.name}</span>
        </p>
        <Outlet />
      </main>
      <AISidePanel mode="customer"
        intro="Hi! I'm the CastleCorp AI concierge. Ask me anything about your job, pricing, or scheduling."
        quickActions={[
          { label: "Track my job", prompt: "Where is my crew right now?" },
          { label: "Rough quote", prompt: "Ballpark price for a 3-seater couch delivered from Rancho Santa Fe to Oceanside?" },
          { label: "Talk to a human", prompt: "I need to speak with someone from the CastleCorp team." },
        ]} />
    </div>
  );
}
